import { Badge, Body1Strong, Caption1, makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';

import { AI_FINDING_PRIORITY_META, AI_FINDING_STATUS_META } from '../constants';
import { getAiFindingSummary } from '../helpers';
import type { TAiFinding } from '../types';
import FindingText from './FindingText';

const useStyles = makeStyles({
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  findingButton: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalL,
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    textAlign: 'left',
    cursor: 'pointer',
    transitionDuration: tokens.durationNormal,
    transitionProperty: 'background-color, border-color, box-shadow, transform',
    transitionTimingFunction: tokens.curveEasyEase,
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      ...shorthands.borderColor(tokens.colorBrandStroke1),
      transform: 'translateY(-1px)',
    },
    '&:focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '2px',
    },
  },
  priorityCritical: {
    boxShadow: `inset 3px 0 0 ${tokens.colorStatusDangerForeground1}`,
  },
  priorityWarning: {
    boxShadow: `inset 3px 0 0 ${tokens.colorStatusWarningForeground1}`,
  },
  priorityInfo: {
    boxShadow: `inset 3px 0 0 ${tokens.colorBrandForeground1}`,
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalM,
  },
  titleGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    minWidth: 0,
  },
  summary: {
    color: tokens.colorNeutralForeground2,
    overflowWrap: 'anywhere',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
    color: tokens.colorNeutralForeground3,
  },
  badgeGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalXS,
  },
});

const PRIORITY_CLASS_MAP = {
  critical: 'priorityCritical',
  warning: 'priorityWarning',
  info: 'priorityInfo',
} as const;

type TProps = {
  findings: TAiFinding[];
  onSelectFinding: (finding: TAiFinding) => void;
};

const MockAiFindingsList = ({ findings, onSelectFinding }: TProps) => {
  const classes = useStyles();

  if (findings.length <= 0) {
    return <Caption1 className="text-neutral-500">No findings are available for this document.</Caption1>;
  }

  return (
    <div className={classes.list}>
      {findings.map((finding) => {
        const priorityMeta = AI_FINDING_PRIORITY_META[finding.priority];
        const statusMeta = finding.status ? AI_FINDING_STATUS_META[finding.status.tone] : undefined;
        const priorityClassName = classes[PRIORITY_CLASS_MAP[finding.priority]];
        const primaryFooterText =
          finding.impact ??
          `${finding.affectedDocs.length} affected document${finding.affectedDocs.length === 1 ? '' : 's'}`;
        const secondaryFooterText = `${finding.suggestedActions.length} recommended action${
          finding.suggestedActions.length === 1 ? '' : 's'
        }`;

        return (
          <button
            key={finding.id}
            type="button"
            className={mergeClasses(classes.findingButton, priorityClassName)}
            onClick={() => onSelectFinding(finding)}
          >
            <div className={classes.cardHeader}>
              <div className={classes.titleGroup}>
                <Body1Strong>{finding.title}</Body1Strong>
                <Caption1 className={classes.summary}>
                  <FindingText text={getAiFindingSummary(finding)} />
                </Caption1>
              </div>
              <div className={classes.badgeGroup}>
                <Badge appearance="tint" color={priorityMeta.badgeColor}>
                  {priorityMeta.label}
                </Badge>
                {finding.status && statusMeta ? (
                  <Badge appearance="outline" color={statusMeta.badgeColor}>
                    {finding.status.label}
                  </Badge>
                ) : null}
              </div>
            </div>

            <div className={classes.footer}>
              <Caption1>{primaryFooterText}</Caption1>
              <Caption1>{secondaryFooterText}</Caption1>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default MockAiFindingsList;
